// components/pages/ChartingV2/CompareChartsModal.tsx

import React, { useEffect, useMemo, useState } from 'react'
import { Combobox, Modal, type ComboboxOption } from '@enamelai/design-system'
import { IPerioChart } from '@dataTypes/chartingFeature.types'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import { formatProviderDisplayName } from '@utils/provider-display.utils'
import { setCompareCharts } from '@store/reducers/charting.reducer'
import './ChartingModals.css'

type ChartOption = ComboboxOption & { chart: IPerioChart }

interface CompareChartsModalProps {
  show: boolean
  onHide: () => void
}

const CompareChartsModal: React.FC<CompareChartsModalProps> = ({
  show,
  onHide,
}) => {
  const dispatch = useAppDispatch()
  const chartHistory: IPerioChart[] = useAppSelector(
    (state) => state.charting.perioCharts,
  )
  const [chartA, setChartA] = useState<IPerioChart | null>(null)
  const [chartB, setChartB] = useState<IPerioChart | null>(null)
  const [chartAInput, setChartAInput] = useState('')
  const [chartBInput, setChartBInput] = useState('')

  const chartOptions: ChartOption[] = useMemo(() => {
    const options = chartHistory
      .slice()
      .sort(
        (a, b) =>
          new Date(b.date_time_created_at ?? 0).getTime() -
          new Date(a.date_time_created_at ?? 0).getTime(),
      )
      .map((chart) => {
        const label = `${new Date(
          chart.date_time_created_at ?? '',
        ).toLocaleDateString(undefined, {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
        })} (${chart.title}) - ${formatProviderDisplayName(chart.provider, 'No provider')}`
        return { value: label, label, chart }
      })

    const labelCounts = options.reduce<Map<string, number>>(
      (counts, option) => {
        counts.set(option.label, (counts.get(option.label) ?? 0) + 1)
        return counts
      },
      new Map(),
    )

    return options.map((option) => {
      if ((labelCounts.get(option.label) ?? 0) < 2) {
        return option
      }
      const uniqueLabel = `${option.label} (${option.chart.chart_id.slice(0, 8)})`
      return { ...option, value: uniqueLabel, label: uniqueLabel }
    })
  }, [chartHistory])

  const chartOptionsForA: ChartOption[] = useMemo(() => {
    return chartOptions.filter(
      (option) => option.chart.chart_id !== chartB?.chart_id,
    )
  }, [chartOptions, chartB?.chart_id])

  const chartOptionsForB: ChartOption[] = useMemo(() => {
    return chartOptions.filter(
      (option) => option.chart.chart_id !== chartA?.chart_id,
    )
  }, [chartOptions, chartA?.chart_id])

  useEffect(() => {
    if (!show) {
      setChartA(null)
      setChartB(null)
      setChartAInput('')
      setChartBInput('')
    }
  }, [show])

  return (
    <Modal
      open={show}
      onClose={onHide}
      size="md"
      className="compare-charts-modal"
      title="Compare charts"
      description="Select two charts to view differences side-by-side."
      primaryLabel="Compare"
      secondaryLabel="Cancel"
      primaryDisabled={
        !chartA || !chartB || chartA.chart_id === chartB.chart_id
      }
      onPrimaryClick={() => {
        onHide()
        if (chartA && chartB && chartA.chart_id !== chartB.chart_id) {
          dispatch(setCompareCharts({ chartA, chartB }))
          setChartA(null)
          setChartB(null)
          setChartAInput('')
          setChartBInput('')
        }
      }}
      onSecondaryClick={onHide}
    >
      <div className="compare-charts-fields">
        <Combobox
          label="Select first chart"
          options={chartOptionsForA}
          allowCustom={false}
          filterable
          placeholder="Select a chart..."
          value={chartAInput}
          onChange={(value) => {
            setChartAInput(value)
            setChartA(
              chartOptionsForA.find((option) => option.value === value)
                ?.chart ?? null,
            )
          }}
        />
        <Combobox
          label="Select second chart"
          options={chartOptionsForB}
          allowCustom={false}
          filterable
          placeholder="Select a chart..."
          value={chartBInput}
          onChange={(value) => {
            setChartBInput(value)
            setChartB(
              chartOptionsForB.find((option) => option.value === value)
                ?.chart ?? null,
            )
          }}
        />
      </div>
    </Modal>
  )
}

export default CompareChartsModal
